import { useState } from "react";
import { ThumbsUp, ThumbsDown } from "@phosphor-icons/react";
import type { Message } from "~/components/assistant/types";
import { cn } from "~/lib/utils";
import rumClient from "~/utils/monitoring";

interface FeedbackButtonsProps {
  message: Message;
  onFeedback: (messageId: string, feedback: "thumbs_up" | "thumbs_down" | null) => Promise<void>;
}

export function FeedbackButtons({ message, onFeedback }: FeedbackButtonsProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState(message.binaryFeedback);

  const handleFeedbackClick = async (feedbackType: "thumbs_up" | "thumbs_down") => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      // If clicking the same button, toggle it off (set to null)
      const newFeedback = currentFeedback === feedbackType ? null : feedbackType;

      // Optimistic update
      setCurrentFeedback(newFeedback);

      // Call the API
      await onFeedback(message.id, newFeedback);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      rumClient?.recordError(error);
      // Revert on error
      setCurrentFeedback(message.binaryFeedback);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center gap-1 pl-2 pt-2">
      <button
        onClick={() => handleFeedbackClick("thumbs_up")}
        disabled={isSubmitting}
        className={cn(
          "p-1 rounded transition-colors hover:bg-gray-100 disabled:opacity-50 cursor-pointer text-gray-800",
          currentFeedback === "thumbs_up" && "text-gray-600"
        )}
        aria-label="Thumbs up"
        title="Helpful"
      >
        <ThumbsUp size={16} weight={currentFeedback === "thumbs_up" ? "fill" : "regular"} />
      </button>
      <button
        onClick={() => handleFeedbackClick("thumbs_down")}
        disabled={isSubmitting}
        className={cn(
          "p-1 rounded transition-colors hover:bg-gray-100 disabled:opacity-50 cursor-pointer text-gray-800",
          currentFeedback === "thumbs_down" && "text-gray-600"
        )}
        aria-label="Thumbs down"
        title="Not helpful"
      >
        <ThumbsDown size={16} weight={currentFeedback === "thumbs_down" ? "fill" : "regular"} />
      </button>
    </div>
  );
}
